import React, { useContext, useEffect, useState } from "react";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { API_BASE_URL, APIs, USERTYPES } from "../../constants/Constants";
import userContext from "../../context/users/UserContext";
import { createPortal } from "react-dom";
import { getEnv } from '../../helpers/EnvHelper';

const UserDataDump = () => {
  const { roles, authenticated } = useContext(userContext);
  const [loading, setLoading] = useState(false);
  const [statusCode, setStatusCode] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");

  const isNotLoggedIn = !authenticated;
  const is403 = statusCode === 403;
  const is401 = statusCode === 401;

  const containerStyle = {
    fontFamily: "Arial, sans-serif",
    padding: "20px",
    backgroundColor: isNotLoggedIn ? "#f5f5f5" : "#1e1e1e",
    border: isNotLoggedIn ? "1px solid #ddd" : "1px solid #333",
    borderRadius: "8px",
    color: isNotLoggedIn ? "#333" : "#ccc",
    maxWidth: "800px",
    margin: "20px auto",
    overflow: "auto",
  };

  const headingStyle = {
    color: isNotLoggedIn ? "#333" : "#f9f9f9",
    marginBottom: "10px",
  };

  const messageStyle = {
    marginTop: "10px",
    fontSize: "14px",
  };

  const spinnerStyle = {
    circle: {
      height: "50px",
      width: "50px",
      borderWidth: "5px",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 1000,
    },
  };

  const startDownload = async () => {
    const pathname = window.location.pathname;
    const search = window.location.search || "";

    if (!pathname.startsWith("/internal/datadump")) {
      setStatusCode(400);
      setStatusMessage(
        "Invalid URL: This page can only be used for /internal/datadump/* URLs."
      );
      return;
    }


    if (!authenticated) {
      setStatusCode(401);
      setStatusMessage("Access is restricted only to logged-in users.");
      return;
    }

    const primaryRole = roles && roles.length > 0 ? roles[0] : null;

    if (
      primaryRole !== USERTYPES.FIRMADMIN &&
      primaryRole !== USERTYPES.ADMIN
    ) {
      setStatusCode(403);
      setStatusMessage("Access is restricted to Firm Admin and Super Admin users.");
      return;
    }

    setLoading(true);
    setStatusCode(null);
    setStatusMessage("");

    try {
      const base =
        API_BASE_URL && API_BASE_URL.endsWith("/")
          ? API_BASE_URL.slice(0, -1)
          : API_BASE_URL || "";
      const pathSuffix = pathname.replace(/^\/internal\/datadump/, "") || "";
      const apiPath = APIs.DATA_DUMP + pathSuffix;
      const backendUrl = `${base}/${apiPath}${search}`;

      const response = await fetch(backendUrl, {
        method: "GET",
        credentials: "include",
      });

      if (response.status === 401) {
        setStatusCode(401);
        setStatusMessage("Access is restricted only to logged-in users.");
        setLoading(false);
        return;
      }

      if (response.status === 403) {
        setStatusCode(403);
        setStatusMessage("Access is restricted to Firm Admin and Super Admin users.");
        setLoading(false);
        return;
      }

      const json = await response.json().catch(() => null);

      if (!response.ok || !json) {
        setStatusCode(response.status);
        setStatusMessage(
          `Error ${response.status}: Unable to download data dump. Please try again or contact support.`
        );
        setLoading(false);
        return;
      }

      if (json.result === "FAILURE") {
        setStatusCode(response.status);
        const dataMessage = json.data && json.data.message;
        if (json.errorCode === "001") {
          setStatusMessage("No data found");
        } else {
          setStatusMessage(
            dataMessage || json.message || "Not processed successfully."
          );
        }
        setLoading(false);
        return;
      }

      if (json.result !== "SUCCESS" || !json.data || !json.data.data) {
        setStatusCode(response.status);
        setStatusMessage(
          (json.data && json.data.message) ||
            json.message ||
            "Unexpected response from server while preparing data dump."
        );
        setLoading(false);
        return;
      }

      const filename =
        (json.data && json.data.filename) || "user-data-dump.csv";

      const base64 = json.data.data;
      const binaryString = window.atob(base64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: "text/csv;charset=utf-8" });

      const url = window.URL.createObjectURL(blob);
      // Only append if url is a safe blob URL
      if (/^blob:/.test(url)) {
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      }
      window.URL.revokeObjectURL(url);

      setStatusCode(200);
      setStatusMessage("Your download has started. You can close this page once it completes.");
    } catch (e) {
      console.error("UserDataDump download error:", e);
      setStatusCode(500);
      setStatusMessage(
        "Unexpected error while downloading the data dump. Please try again or contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    startDownload();
  }, []);

  return (
    <div
      className="main-container-body"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "14px",
        minHeight: "100vh",
        backgroundColor: isNotLoggedIn ? "#e8e8e8" : undefined,
        position: "relative",
      }}
    >
      {loading &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(0,0,0,0.4)",
              pointerEvents: "auto",
            }}
          >
            <Spinner size={SpinnerSize.small} styles={spinnerStyle} />
          </div>,
          document.body
        )}

      <div
        style={{
          ...containerStyle,
          pointerEvents: loading ? "none" : "auto",
          opacity: loading ? 0.6 : 1,
        }}
      >
        <h2 style={headingStyle}>User Data Dump Download</h2>
        {is403 ? (
          <div style={messageStyle}>
            <span>
              {statusMessage ||
                "Access is restricted to Firm Admin and Super Admin users."}
            </span>
          </div>
        ) : is401 ? (
          <div style={messageStyle}>
            <span>
              {statusMessage || "Access is restricted only to logged-in users."}
            </span>
          </div>
        ) : (
          <>
            <div style={messageStyle}>
              {statusMessage && <span>{statusMessage}</span>}
              {!statusMessage && !loading && (
                <span>
                  Preparing your download. If it doesn&apos;t start
                  automatically, please refresh the page.
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserDataDump;

